import { classNames } from '@commenda/utils';
import { flexRender, Table } from '@tanstack/react-table';
import React from 'react';
import { Checkbox } from '../../Checkbox';
import { ContextMenu, ContextMenuMenuProps } from '../../ContextMenu';
import { TableCell as TCell, TableRow as TRow } from '../../Table';
import { ColumnGroup, ColumnResizeControl, RowHeightView } from '../types';
import {
  FLEX_SPACER_BODY_CLASSNAME,
  FLEX_SPACER_STYLE,
  getAlignClass,
  getColumnId,
  getColumnWidthStyle,
  getSpannedWidth,
  getStickyColumnClasses,
  getStickyPositionLive,
  getStickyPositionVar,
  shouldRenderCell,
} from '../utils';
import { ColumnResizeHandle } from './ColumnResizeHandle';

// Wraps a row's <tr> in a ContextMenu and forces the hover background while
// the menu is open — the cursor moves into the portal so :hover is lost.
function RowMenuWrapper({
  menu,
  trElement,
}: {
  menu: ContextMenuMenuProps;
  trElement: React.ReactElement;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <ContextMenu menu={menu} onOpenChange={setOpen}>
      {open
        ? React.cloneElement(trElement, { 'data-row-hovered': 'true' })
        : trElement}
    </ContextMenu>
  );
}

// Mirrors :hover for rows with data-row-hovered (set via isRowHovered).
// Plain CSS — Tailwind data-variants didn't carry across the package boundary.
const ROW_HOVER_STYLE_ID = 'kakashi-datatable-row-hover';
const ROW_HOVER_CSS = `
tr[data-row-hovered='true'],
tr[data-row-hovered='true'] > td.sticky {
  background-color: var(--color-bg-primary-hover);
}
`;
let rowHoverStyleInjected = false;
const ensureRowHoverStyle = () => {
  if (rowHoverStyleInjected || typeof document === 'undefined') return;
  if (document.getElementById(ROW_HOVER_STYLE_ID)) {
    rowHoverStyleInjected = true;
    return;
  }
  const el = document.createElement('style');
  el.id = ROW_HOVER_STYLE_ID;
  el.textContent = ROW_HOVER_CSS;
  document.head.appendChild(el);
  rowHoverStyleInjected = true;
};

export function TableRow<TData, TValue>({
  table,
  groups,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  isRowHovered,
  rowClassName,
  rowDataAttributes,
  stickyRowBgClassName,
  rowHeightView,
  tableCellClassName,
  tableRowClassName,
  columnResizeConfig,
  columnResizeControls,
  expandable,
  rowSelectionControl,
  rowContextMenu,
}: {
  table: Table<TData>;
  groups: ColumnGroup<TData, TValue>[];
  onRowClick?: (row: TData) => void;
  onRowMouseEnter?: (row: TData) => void;
  onRowMouseLeave?: (row: TData) => void;
  isRowHovered?: (row: TData) => boolean;
  rowClassName: (row: TData) => string;
  rowContextMenu?: (row: TData) => ContextMenuMenuProps | undefined;
  // Extra data-* attributes spread onto each row's <tr>. See DataTableProps.
  rowDataAttributes?: (row: TData) => Record<string, string> | undefined;
  // Per-row override for the sticky-cell background. Returning undefined keeps
  // the kakashi default (bg-bg-primary). Use this when rowClassName paints a
  // tint that the sticky cell's hardcoded bg would otherwise mask.
  stickyRowBgClassName?: (row: TData) => string | undefined;
  rowHeightView: RowHeightView;
  tableCellClassName: string;
  tableRowClassName: string;
  columnResizeConfig?: { isEnabled: boolean };
  /**
   * Per-column resize controls (keyed by column id). When present for a column,
   * its body cells render the same drag handle as the header so the edge can be
   * grabbed from any row.
   */
  columnResizeControls?: Record<string, ColumnResizeControl>;
  /** Read only by the memoized wrapper's comparator to freeze rows mid-resize. */
  isResizingColumn?: boolean;
  expandable?: {
    expandedIdSet: Set<string>;
    toggleExpandById: (id: string) => void;
    getRowId: (row: TData) => string;
    renderExpandedContent: (row: TData) => React.ReactNode;
    maxExpandedHeight: number;
    borderClassName?: string;
    isRowExpandable?: (row: TData) => boolean;
  };
  rowSelectionControl?: {
    isEnabled: boolean;
    isRowSelected: (row: TData) => boolean;
    toggleRowById: (id: string) => void;
    toggleAll: (subset?: TData[]) => void;
    getRowId: (row: TData) => string;
  };
}) {
  const rowHeight = rowHeightView === 'expanded' ? '!h-[52px]' : '!h-11';
  const totalColumns = groups.flatMap((g) => g.columns).length;
  const rightStickyGroupId = groups.find((g) => g.sticky === 'right')?.id;
  // +1 for the trailing flex-spacer cell on the resize path.
  const expandedColSpan =
    totalColumns + (columnResizeConfig?.isEnabled ? 1 : 0);
  ensureRowHoverStyle();

  return (
    <>
      {table.getRowModel().rows.map((row) => {
        const ctxMenu = rowContextMenu?.(row.original);
        const trElement = (
          <TRow
            key={row.id}
            {...rowDataAttributes?.(row.original)}
            data-row-hovered={isRowHovered?.(row.original) ? 'true' : undefined}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}
            onMouseEnter={
              onRowMouseEnter ? () => onRowMouseEnter(row.original) : undefined
            }
            onMouseLeave={
              onRowMouseLeave ? () => onRowMouseLeave(row.original) : undefined
            }
            className={classNames(
              // Hover bg unconditional — sticky cells already react via
              // group-hover, so gating on onRowClick made non-sticky cells
              // look transparent on rows without a click handler.
              'border-b border-border-primary last:border-b-0 group hover:bg-bg-primary-hover',
              onRowClick ? 'cursor-pointer' : 'cursor-default',
              rowClassName(row.original),
              tableRowClassName
            )}
          >
            {groups.map((group, groupIndex) => {
              const cells = group.columns.map((column, columnIndex) => {
                const columnId = getColumnId(column);

                // Skip if column is not visible
                if (!table.getColumn(columnId)?.getIsVisible()) {
                  return null;
                }

                const cell = row
                  .getAllCells()
                  .find((c) => c.column.id === columnId);
                if (!cell) return null;

                // Check if this cell should be rendered based on spanning
                if (
                  !shouldRenderCell({
                    row,
                    columnIndex,
                    groupIndex,
                    table,
                    groups,
                  })
                ) {
                  return null;
                }

                // Calculate span only if the column is visible and has getSpan
                let colSpan = 1;
                let totalWidth = column.width || 0;
                let spanClassName = '';

                if (column.getSpan) {
                  const span = column.getSpan(row.original);
                  if (span) {
                    colSpan = span.colSpan;
                    spanClassName = span.className || '';
                    totalWidth = getSpannedWidth({
                      column,
                      columnIndex,
                      groupIndex,
                      row,
                      groups,
                    });
                  }
                }

                // Use totalWidth if available, otherwise use live size (resize) or column.width
                const effectiveWidth =
                  totalWidth > 0 ? totalWidth : column.width;
                const widthStyle = getColumnWidthStyle(
                  effectiveWidth,
                  columnResizeConfig?.isEnabled,
                  cell.column.getSize(),
                  cell.column.id,
                  // Resize path: lock every column so the spacer absorbs the slack.
                  columnResizeConfig?.isEnabled || !!group.sticky
                );

                const firstVisibleCellCol = groups
                  .flatMap((g) => g.columns)
                  .find((c) =>
                    table.getColumn(getColumnId(c))?.getIsVisible?.()
                  );
                const isFirstVisibleCell =
                  !!firstVisibleCellCol &&
                  getColumnId(firstVisibleCellCol) === columnId;

                // Check if this is the last visible column
                const allVisibleColumns = groups
                  .flatMap((g) => g.columns)
                  .filter((c) =>
                    table.getColumn(getColumnId(c))?.getIsVisible?.()
                  );
                const isLastVisibleCell =
                  allVisibleColumns.length > 0 &&
                  getColumnId(
                    allVisibleColumns[allVisibleColumns.length - 1]
                  ) === columnId;

                // For sticky groups, determine if this is the edge column (for separator shadow)
                const visibleGroupColumns = group.columns.filter((c) =>
                  table.getColumn(getColumnId(c))?.getIsVisible?.()
                );
                const isFirstInGroup = visibleGroupColumns[0] === column;
                const isLastInGroup =
                  visibleGroupColumns[visibleGroupColumns.length - 1] ===
                  column;

                return (
                  <TCell
                    key={`${group.id}-${groupIndex}-${columnIndex}-${getColumnId(column)}`}
                    colSpan={colSpan}
                    className={classNames(
                      'px-4 relative',
                      // Override padding to control row height precisely
                      rowHeightView === 'expanded' ? 'py-2.5' : 'py-0',
                      // Non-sticky columns: no vertical borders
                      '',
                      getStickyColumnClasses(group, {
                        isHeader: false,
                        bgClassName: stickyRowBgClassName?.(row.original),
                      }),
                      rowHeight || '',
                      tableCellClassName,
                      spanClassName
                    )}
                    style={{
                      ...widthStyle,
                      ...(group.sticky === 'left' && {
                        left: columnResizeConfig?.isEnabled
                          ? getStickyPositionVar(table, groups, group, columnIndex)
                          : `${getStickyPositionLive(table, groups, group, columnIndex)}px`,
                      }),
                      ...(group.sticky === 'right' && {
                        right: columnResizeConfig?.isEnabled
                          ? getStickyPositionVar(table, groups, group, columnIndex)
                          : `${getStickyPositionLive(table, groups, group, columnIndex)}px`,
                      }),
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0 h-full">
                      {isFirstVisibleCell && rowSelectionControl?.isEnabled ? (
                        <Checkbox
                          className="checkbox-cell shrink-0"
                          aria-label="Select row"
                          checked={rowSelectionControl.isRowSelected(
                            row.original
                          )}
                          onCheckedChange={() =>
                            rowSelectionControl.toggleRowById(
                              rowSelectionControl.getRowId(row.original)
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : null}
                      <div
                        data-dt-col={columnId}
                        className={classNames(
                          'min-w-0 flex-1 h-full flex items-center',
                          getAlignClass(column.align),
                          columnResizeConfig?.isEnabled
                            ? 'overflow-hidden [&>*]:truncate [&>*]:min-w-0'
                            : ''
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    </div>
                    {columnResizeControls?.[columnId] && (
                      <ColumnResizeHandle
                        control={columnResizeControls[columnId]}
                      />
                    )}
                  </TCell>
                );
              });
              return (
                <React.Fragment key={group.id}>
                  {columnResizeConfig?.isEnabled &&
                    group.id === rightStickyGroupId && (
                      <TCell
                        key={`${group.id}-flex-spacer`}
                        aria-hidden
                        className={FLEX_SPACER_BODY_CLASSNAME}
                        style={FLEX_SPACER_STYLE}
                      />
                    )}
                  {cells}
                </React.Fragment>
              );
            })}
            {columnResizeConfig?.isEnabled && !rightStickyGroupId && (
              <TCell
                key="flex-spacer-trailing"
                aria-hidden
                className={FLEX_SPACER_BODY_CLASSNAME}
                style={FLEX_SPACER_STYLE}
              />
            )}
          </TRow>
        );
        return (
          <React.Fragment key={row.id}>
            {ctxMenu ? (
              <RowMenuWrapper menu={ctxMenu} trElement={trElement} />
            ) : (
              trElement
            )}
            {expandable &&
              (() => {
                const rowId = expandable.getRowId(row.original);
                const canExpand = expandable.isRowExpandable
                  ? expandable.isRowExpandable(row.original)
                  : true;
                if (!canExpand) return null;
                if (!expandable.expandedIdSet.has(rowId)) return null;
                return (
                  <>
                    <TRow>
                      <TCell colSpan={expandedColSpan} className="p-0">
                        <div
                          className="w-full"
                          style={{
                            maxHeight: `${expandable.maxExpandedHeight}px`,
                            overflowY: 'auto',
                          }}
                        >
                          {expandable.renderExpandedContent(row.original)}
                        </div>
                      </TCell>
                    </TRow>
                    <TRow>
                      <TCell
                        colSpan={expandedColSpan}
                        className={classNames(
                          'h-0 p-0 border-b-3 border-border-primary',
                          expandable.borderClassName || ''
                        )}
                      />
                    </TRow>
                  </>
                );
              })()}
          </React.Fragment>
        );
      })}
    </>
  );
}
